<template>
  <div class="vision-dashboard-page">
    <!-- Page Header -->
    <div class="db-page-header">
      <div class="db-page-header-left">
        <img src="@/assets/img/camera.png" class="db-header-icon" />
        <h2 class="page-title-bar">{{ $t("Dashboard") }}</h2>
      </div>
    </div>

    <!-- Tabs -->
    <div style="display: none;">
      <v-tabs v-model="activeTab" hide-slider>
        <v-tab v-for="tab in tabs" :key="tab" class="db-tab">{{ tab }}</v-tab>
      </v-tabs>
    </div>

    <!-- Tab 0: Main Dashboard -->
    <div v-if="activeTab === 0" class="db-content">

      <!-- Shelf Tasks -->
      <div class="db-section-label">
        <i class="mdi mdi-view-grid-outline db-section-icon"></i>{{ $t("Shelf Task") }}
      </div>
      <div class="shelf-task-grid">
        <div
          v-for="task in shelfTasks"
          :key="task.title"
          class="stask-card"
          v-ripple
          role="button"
          tabindex="0"
          @click="handleTaskClick(task)"
          @keydown.enter="handleTaskClick(task)"
          @keydown.space.prevent="handleTaskClick(task)"
        >
          <div class="stask-accent" :style="{ background: task.accentColor }"></div>
          <div class="stask-body">
            <div class="stask-title">{{ task.title }}</div>
            <div class="stask-pct">{{ task.percentage }}</div>
            <div class="stask-count">{{ task.count }} <span>/ {{ task.total }} ea</span></div>
          </div>
        </div>
      </div>

      <!-- Shelf Info + Device Health -->
      <div class="db-two-col-header">
        <div class="db-section-label mb-0">
          <i class="mdi mdi-layers-outline db-section-icon"></i>{{ $t("Shelf Info") }}
        </div>
        <div class="db-section-label mb-0">
          <i class="mdi mdi-heart-pulse db-section-icon"></i>{{ $t("Device Health Check") }}
        </div>
      </div>
      <div class="info-summary-grid">
        <div class="shelf-info-card info-grid-card">
          <div class="shelf-info-split">
            <div class="shelf-info-left pa-4">
              <div class="ig-label">SKUs</div>
              <div class="ig-value">{{ shelfInfo.skus || 0 }}</div>
            </div>
            <div class="shelf-info-right pa-4">
              <div class="ig-label">{{ $t("Vision Coverage") }}</div>
              <div class="vision-coverage-row mt-3">
                <div class="vision-coverage-item">
                  <div class="ig-sublabel">{{ $t("Shelf Groups") }}</div>
                  <div class="ig-subvalue">{{ shelfInfo.shelfGroups || 0 }}</div>
                </div>
                <div class="vision-coverage-item">
                  <div class="ig-sublabel">{{ $t("Shelves") }}</div>
                  <div class="ig-subvalue">{{ shelfInfo.shelves || 0 }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="device-health-card info-grid-card pa-4">
          <div class="hc-title"><i class="mdi mdi-wifi hc-icon"></i>{{ $t("Connectivity") }}</div>
          <div class="health-card-stats mt-3">
            <div v-for="item in connectivity" :key="item.label" class="health-stat-item" :class="{ 'no-border-right': item.label === 'Bad' }">
              <div class="hc-badge" :class="'hc-badge--' + item.label.toLowerCase()">{{ item.label }}</div>
              <div class="health-number" :class="{ greenColor: item.label === 'Good', colorRed: item.label === 'Bad' }">{{ item.value || 0 }}</div>
            </div>
          </div>
        </div>

        <div class="device-health-card info-grid-card pa-4">
          <div class="hc-title"><i class="mdi mdi-battery-high hc-icon"></i>{{ $t("Battery") }}</div>
          <div class="health-card-stats mt-3">
            <div v-for="item in battery" :key="item.label" class="health-stat-item" :class="{ 'no-border-right': item.label === 'Bad' }">
              <div class="hc-badge" :class="'hc-badge--' + item.label.toLowerCase()">{{ item.label }}</div>
              <div class="health-number" :class="{ greenColor: item.label === 'Good', colorRed: item.label === 'Bad' }">{{ item.value || 0 }}</div>
            </div>
          </div>
        </div>

        <div class="device-health-card info-grid-card pa-4">
          <div class="hc-title"><i class="mdi mdi-server hc-icon"></i>{{ $t("Edge Devices") }}</div>
          <div class="edge-device-info mt-3">
            <div class="edge-row">
              <span class="edge-key">Ver</span>
              <span class="edge-val">{{ edgeDevice.version || "N/A" }}</span>
            </div>
            <div class="edge-row">
              <span class="edge-key">Status</span>
              <span class="edge-val edge-status" :class="{ 'edge-status--ok': edgeDevice.status === 'OK' }">
                <i class="mdi" :class="edgeDevice.status === 'OK' ? 'mdi-check-circle' : 'mdi-minus-circle'"></i>
                {{ edgeDevice.status }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Operation Info -->
      <div class="db-section-label mt-4">
        <i class="mdi mdi-chart-line db-section-icon"></i>{{ $t("Operation Info") }}
      </div>
      <div class="operation-info-row">
        <div v-for="item in operationInfo" :key="item.label" class="operation-card">
          <div class="op-header">
            <span class="op-label">{{ item.label }}</span>
            <span class="op-trend" :class="item.trend >= 0 ? 'op-trend--up' : 'op-trend--down'">
              <i class="mdi" :class="item.trend >= 0 ? 'mdi-trending-up' : 'mdi-trending-down'"></i>
              {{ item.trend ? Math.abs(item.trend).toFixed(2) : "0.00" }}%
            </span>
          </div>
          <div class="op-value">{{ item.value }}</div>
          <div class="op-avg">{{ $t("Average") }}: <strong>{{ item.average }}</strong></div>
        </div>
      </div>

      <!-- Shelf History -->
      <div class="shelf-history-card mt-4">
        <div class="shelf-history-header">
          <div class="db-section-label mb-0">
            <i class="mdi mdi-chart-bar db-section-icon"></i>{{ $t("Shelf History") }}
          </div>
          <div class="shelf-history-controls">
            <div class="range-switcher">
              <button v-for="range in ranges" :key="range" class="range-btn" :class="{ 'range-btn--active': selectedRange === range }" @click="selectedRange = range">{{ range }}</button>
            </div>
            <div class="date-picker-wrapper">
              <v-menu v-model="dateMenu" :close-on-content-click="false" transition="scale-transition" offset-y min-width="290px">
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field :value="selectedDate" v-bind="attrs" v-on="on" class="form-input" outlined dense hide-details readonly>
                    <template v-slot:append><img width="16" height="16" src="@/assets/img/ico-calendar.png" /></template>
                  </v-text-field>
                </template>
                <v-date-picker v-model="selectedDate" :locale="languageCodes[$store.getters['dataStore/GET_CURRENT_LANGUAGE']]" @input="dateMenu = false" no-title />
              </v-menu>
            </div>
          </div>
        </div>
        <div v-if="shelfHistorySeries.length">
          <apexchart class="ml-n4" type="bar" height="320" :options="shelfHistoryOptions" :series="shelfHistorySeries" />
        </div>
        <div v-else class="empty-chart-state">
          <i class="mdi mdi-chart-bar empty-chart-icon"></i>
          <p class="empty-text">{{ $t("No data available") }}</p>
        </div>
      </div>
    </div>

    <!-- Tab 1: OOS Analytics -->
    <div v-if="activeTab === 1" class="db-content">
      <div class="row">
        <div class="col-lg-6 col-12 pr-0">
          <div class="dashboard-card">
            <div class="db-section-label mb-3"><i class="mdi mdi-gauge db-section-icon"></i>{{ $t("Score") }}</div>
            <div class="score-header"><span class="score-value">0%</span><span class="greenColor ml-2">0%</span></div>
            <div class="row">
              <div class="col-md-4">
                <apexchart type="donut" height="180" :options="scoreChartOptions" :series="scoreSeries" />
              </div>
              <div class="col-md-8">
                <v-simple-table>
                  <thead><tr><th></th><th></th><th>{{ $t("Last Month") }}</th></tr></thead>
                  <tbody>
                    <tr v-for="item in scoreTable" :key="item.name">
                      <td><span class="dot" :style="{ background: item.color }" />{{ item.name }}</td>
                      <td><strong>{{ item.current }}</strong></td>
                      <td>{{ item.previous }}</td>
                    </tr>
                  </tbody>
                </v-simple-table>
              </div>
            </div>
          </div>
          <div class="row mt-3 mx-0">
            <div v-for="(tbl, idx) in [{h: sectionHeaders, i: topSections, t: 'Top 5 OoS Section'}, {h: brandHeaders, i: topBrands, t: 'Top 5 OoS Brand'}, {h: productHeaders, i: topProducts, t: 'Top 5 OoS Product'}]" :key="idx" class="col-md-4 pa-0" :class="{ 'pl-1': idx > 0 }">
              <div class="dashboard-card rounded-0 pa-0">
                <h4 class="card-title">{{ $t(tbl.t) }}</h4>
                <v-data-table :headers="tbl.h" :items="tbl.i" class="tbl-type01" hide-default-footer dense>
                  <template v-slot:no-data>
                    <div class="px-2 no-data"><v-icon>mdi-alert-outline</v-icon><span class="ml-1">{{ $t("No data available") }}</span></div>
                  </template>
                </v-data-table>
              </div>
            </div>
          </div>
        </div>
        <div class="col-lg-6 col-12">
          <div class="dashboard-card">
            <div class="floor-header">
              <div class="db-section-label mb-0"><i class="mdi mdi-map-outline db-section-icon"></i>{{ $t("Floor Map") }}</div>
              <v-btn icon><v-icon>mdi-fullscreen</v-icon></v-btn>
            </div>
            <div class="floor-toolbar">
              <div class="legend-group">
                <span><i class="dot oos"></i> OoS</span>
                <span><i class="dot ls"></i> LS</span>
                <span><i class="dot mis"></i> Mis</span>
              </div>
              <div class="d-flex align-center" style="gap:8px">
                <v-select v-model="selectedFloor" :items="floors" dense outlined hide-details style="max-width:120px" />
                <v-btn icon small @click="zoomIn"><v-icon small>mdi-magnify-plus</v-icon></v-btn>
                <v-btn icon small @click="zoomOut"><v-icon small>mdi-magnify-minus</v-icon></v-btn>
              </div>
            </div>
            <div class="floor-map-container">
              <svg viewBox="0 0 200 200" height="100%" width="100%" :style="{ transform: `scale(${zoomLevel})`, transformOrigin: 'center center', transition: 'transform 0.2s ease' }">
                <rect x="25" y="27" width="100" height="50" fill="#E5FFF7" stroke="#606060" />
                <rect x="25" y="122" width="100" height="50" fill="#E5FFF7" stroke="#606060" />
                <text x="27" y="33">A01-001</text>
                <text x="27" y="128">A01-002</text>
              </svg>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import VueApexCharts from 'vue-apexcharts'
import codes from '../../plugins/codes'
import { mapGetters } from 'vuex'
import debounce from 'lodash/debounce'
import EventBus from '@/plugins/eventBus'
export default {
  name: 'VisionDashboard',
  components: {
    apexchart: VueApexCharts
  },
  data () {
    return {
      activeTab: 0,
      tabs: [this.$t('Dashboard')],
      oosCount: 0,
      misCount: 0,
      lowCount: 0,
      eslMisCount: 0,
      skuCount: 0,
      shelfGroupCount: 0,
      shelvesCount: 0,
      labelCount: 0,
      cameraCount: 0,
      signalGoodCount: 0,
      signalBadCount: 0,
      batteryGoodCount: 0,
      batteryBadCount: 0,
      edgeVer: '',
      edgeStatus: 0,
      duration: 0,
      durationAvg: 0,
      durationMore: 0,
      rate: 0,
      rateAvg: 0,
      rateMore: 0,
      shelfHistorySeries: [],

      shelfHistoryOptions: {
        chart: {
          stacked: true,
          toolbar: {
            show: false
          },
          width: '100%'
        },
        plotOptions: {
          bar: {
            horizontal: false,
            columnWidth: '20%'
          }
        },
        colors: ['#E76067', '#5B9BD5', '#E0C800', '#8B8B8B'],
        xaxis: {
          categories: []
        },
        legend: {
          position: 'top',
          horizontalAlign: 'left',
          fontSize: '12px',
          markers: {
            height: 8,
            width: 8
          },
          labels: {
            colors: '#7a869a'
          }
        },
        dataLabels: {
          enabled: false
        },
        stroke: {
          width: 0
        },
        grid: {
          borderColor: '#E5E7EB'
        },
        yaxis: {
          labels: {
            style: {
              fontSize: '12px'
            }
          }
        },
        tooltip: {
          shared: true,
          intersect: false
        }
      },
      selectedRange: 'Day',
      ranges: ['Day', 'Week', 'Month'],
      languageCodes: {
        English: 'en',
        한국어: 'ko',
        Deutsch: 'de',
        Francais: 'fr',
        中文: 'zh',
        Espanol: 'es',
        Italiano: 'it',
        Portugal: 'por',
        日本語: 'jp'
      },
      dateMenu: false,
      selectedDate: new Date().toISOString().substr(0, 10),
      selectedFloor: '1F',
      floors: ['1F', '2F', '3F'],

      scoreSeries: [70, 20, 10],

      scoreChartOptions: {
        labels: ['OK', 'Out of Stock', 'Others'],
        colors: ['#75B0DD', '#F0806F', '#87BF74'],
        legend: {
          show: false
        }
      },

      scoreTable: [
        {
          name: 'OK',
          current: 0,
          previous: 0,
          color: '#75B0DD'
        },
        {
          name: 'Out of Stock',
          current: 0,
          previous: 0,
          color: '#F0806F'
        },
        {
          name: 'Others',
          current: 0,
          previous: 0,
          color: '#87BF74'
        }
      ],

      sectionHeaders: [
        { text: '#', value: 'id' },
        { text: 'Section Name', value: 'name' }
      ],

      brandHeaders: [
        { text: '#', value: 'id' },
        { text: 'Brand Name', value: 'name' }
      ],

      productHeaders: [
        { text: '#', value: 'id' },
        { text: 'Product Name', value: 'name' }
      ],

      topSections: [],
      topBrands: [],
      topProducts: [],
      zoomLevel: 1
    }
  },
  watch: {
    store: {
      handler () {
        if (!this.store.code) {
          EventBus.$emit('openSelectedStores')
          this.oosCount = 0
          this.misCount = 0
          this.lowCount = 0
          this.eslMisCount = 0
          this.skuCount = 0
          this.shelfGroupCount = 0
          this.shelvesCount = 0
          this.labelCount = 0
          this.cameraCount = 0
          this.signalGoodCount = 0
          this.signalBadCount = 0
          this.batteryGoodCount = 0
          this.batteryBadCount = 0
          this.edgeVer = ''
          this.edgeStatus = 0
          this.duration = 0
          this.durationAvg = 0
          this.durationMore = 0
          this.rate = 0
          this.rateAvg = 0
          this.rateMore = 0
          this.shelfHistorySeries = []
          return
        }
        this.fetchDashboardSummary()
        this.fetchShelfHistory('d')
        this.selectedRange = 'Day'
      }
    },
    selectedRange (value) {
      let type = 'w'
      if (value === 'Day') {
        type = 'd'
      } else if (value === 'Month') {
        type = 'm'
      }
      this.fetchShelfHistory(type)
    },
    selectedDate () {
      this.fetchShelfHistory('d')
      this.selectedRange = 'Day'
    }
  },
  computed: {
    ...mapGetters({
      store: 'dataStore/GET_SELECTED_STORE'
    }),

    shelfTasks () {
      return [
        {
          title: this.$t('Out of Stock'),
          route: { name: 'Shelf Monitoring', params: { error: 'oos' } },
          percentage: `${
            this.skuCount
              ? ((this.oosCount / this.skuCount) * 100).toFixed(1)
              : 0
          }%`,
          count: this.oosCount,
          total: this.skuCount,
          accentColor: '#E76067'
        },
        {
          title: this.$t('Low Stock'),
          route: { name: 'Shelf Monitoring', params: { error: 'ls' } },
          percentage: `${
            this.skuCount
              ? ((this.lowCount / this.skuCount) * 100).toFixed(1)
              : 0
          }%`,
          count: this.lowCount,
          total: this.skuCount,
          accentColor: '#5B9BD5'
        },
        {
          title: this.$t('Misplaced'),
          route: { name: 'Shelf Monitoring', params: { error: 'mis' } },
          percentage: `${
            this.skuCount
              ? ((this.misCount / this.skuCount) * 100).toFixed(1)
              : 0
          }%`,
          count: this.misCount,
          total: this.skuCount,
          accentColor: '#E0C800'
        },
        {
          title: this.$t('ESL MisDetection'),
          route: { name: 'Realogram', params: { error: 'eslMis' } },
          percentage: `${
            this.labelCount
              ? ((this.eslMisCount / this.labelCount) * 100).toFixed(1)
              : 0
          }%`,
          count: this.eslMisCount,
          total: this.labelCount,
          accentColor: '#8B8B8B'
        }
      ]
    },

    shelfInfo () {
      return {
        skus: this.skuCount,
        shelfGroups: this.shelfGroupCount,
        shelves: this.shelvesCount
      }
    },

    connectivity () {
      return [
        {
          label: 'Total',
          value: this.cameraCount
        },
        {
          label: 'Good',
          value: this.signalGoodCount
        },
        {
          label: 'Bad',
          value: this.signalBadCount
        }
      ]
    },

    battery () {
      return [
        {
          label: 'Total',
          value: this.cameraCount
        },
        {
          label: 'Good',
          value: this.batteryGoodCount
        },
        {
          label: 'Bad',
          value: this.batteryBadCount
        }
      ]
    },

    edgeDevice () {
      return {
        version: this.edgeVer,
        status: this.edgeStatus === 1 ? 'OK' : 'N/A'
      }
    },

    operationInfo () {
      return [
        {
          label: 'OOS Duration',
          value: this.duration ? `${this.duration.toFixed(2)}h` : '0.00h',
          average: this.durationAvg || 0,
          trend: this.durationMore || 0
        },
        {
          label: 'OOS Rate',
          value: this.rate ? `${this.rate.toFixed(2)}%` : '0.00%',
          average: this.rateAvg || 0,
          trend: this.rateMore || 0
        }
      ]
    }
  },
  mounted () {
    EventBus.$emit('enableSelectedStores', true)
    if (!this.store.code) {
      EventBus.$emit('openSelectedStores')
    } else {
      this.getVisionConfig(this.store.code)
      this.fetchDashboardSummary()
      this.fetchShelfHistory('d')
    }
  },
  methods: {
    getVisionConfig (store) {
      const config = { params: { stationCode: store } }
      this.$utils.callAxios(codes.requests.getVisionConfig.method, codes.requests.getVisionConfig.url, config)
        .then((res) => {
          if (res.status === 200 && !res.data?.visionEnabled) {
            this.$store.dispatch('dataStore/updateShowVisionModal', true)
          }
        })
        .catch((error) => {
          console.warn(`Could not find Vision Config : ${error}`)
        })
    },
    handleTaskClick (task) {
      this.$router.push(task.route).catch(error => {
        console.warn(`Failed to redirect shelf task. error: ${error}`)
      })
    },
    zoomIn () {
      this.zoomLevel += 0.2
    },
    zoomOut () {
      if (this.zoomLevel > 0.4) {
        this.zoomLevel -= 0.2
      }
    },
    fetchDashboardSummary: debounce(function () {
      const config = { params: {} }
      config.params.store = this.store.code
      this.$utils
        .callAxios(
          codes.requests.togetVisionSummary.method,
          codes.requests.togetVisionSummary.url,
          config
        )
        .then(res => {
          if (res.data) {
            this.mapSummaryData(res.data.summaryData)
          }
        })
        .catch(error => {
          console.error('Error fetching Summary:', error)
        })
    }, 100),
    mapSummaryData (data) {
      this.oosCount = data.oosCount
      this.misCount = data.misCount
      this.lowCount = data.lowCount
      this.eslMisCount = data.eslMisCount
      this.skuCount = data.skuCount
      this.shelfGroupCount = data.shelfGroupCount
      this.shelvesCount = data.shelvesCount
      this.labelCount = data.labelCount
      this.cameraCount = data.cameras.count
      this.signalBadCount = data.cameras.signalBadCount
      this.signalGoodCount = data.cameras.signalGoodCount
      this.batteryBadCount = data.cameras.batteryBadCount
      this.batteryGoodCount = data.cameras.batteryGoodCount
      this.edgeVer = data.edgeDevices.version
      this.edgeStatus = data.edgeDevices.status
      this.duration = data?.oosDuration?.duration
      this.durationAvg = data?.oosDuration?.avg
      this.durationMore = data?.oosDuration?.more
      this.rate = data?.oosRate?.rate
      this.rateAvg = data?.oosRate?.avg
      this.rateMore = data?.oosRate?.more
    },
    fetchShelfHistory: debounce(function (type = 'd') {
      const config = {
        params: {
          store: this.store.code,
          type,
          date: this.selectedDate
        }
      }
      if (type === 'd') {
        config.params.timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone
      }
      this.$utils
        .callAxios(
          codes.requests.togetShelfHistory.method,
          codes.requests.togetShelfHistory.url,
          config
        )
        .then(res => {
          if (res.data) {
            this.mapShelfHistory(res.data.data)
          }
        })
        .catch(error => {
          console.error('Error fetching Shelf History:', error)
        })
    }, 100),
    mapShelfHistory (data) {
      if (!data.length) {
        this.shelfHistorySeries = []
        this.shelfHistoryOptions = {
          ...this.shelfHistoryOptions,
          xaxis: {
            ...this.shelfHistoryOptions.xaxis,
            categories: []
          }
        }
        return
      }
      this.shelfHistoryOptions = {
        ...this.shelfHistoryOptions,
        xaxis: {
          ...this.shelfHistoryOptions.xaxis,
          categories: data.map(item => item.date)
        }
      }
      this.shelfHistorySeries = [
        {
          name: 'OOS',
          data: data.map(item => item.oosCount)
        },
        {
          name: 'Low Stock',
          data: data.map(item => item.lowCount)
        },
        {
          name: 'Misplaced',
          data: data.map(item => item.misCount)
        },
        {
          name: 'ESL Misplaced',
          data: data.map(item => item.eslMisCount)
        }
      ]
    }
  }
}
</script>

<style scoped>
/* ── Layout ──────────────────────────────────────────────── */
.vision-dashboard-page { min-height: 100vh; }
.db-content { margin-top: 20px; }

/* ── Header ──────────────────────────────────────────────── */
.db-page-header { display: flex; align-items: center; justify-content: space-between; padding-bottom: 8px; margin-bottom: 4px; }
.db-page-header-left { display: flex; align-items: center; gap: 10px; }
.db-header-icon { width: 28px; height: 28px; object-fit: contain; }
.db-page-title { font-size: 20px; font-weight: 700; color: #001E38; margin: 0; letter-spacing: -0.3px; }

/* ── Tabs ────────────────────────────────────────────────── */
.db-tab { text-transform: none; font-size: 13px; font-weight: 600; color: #636363; letter-spacing: 0; min-width: 0; padding: 0 16px; }
::v-deep .v-tab--active.db-tab { color: #001E38 !important; border-bottom: 2px solid #001E38; }
::v-deep .v-tabs-slider-wrapper { display: none; }
::v-deep .v-slide-group__wrapper { height: 38px; }

/* ── Section labels ──────────────────────────────────────── */
.db-section-label { display: flex; align-items: center; gap: 6px; font-size: 13px; font-weight: 700; color: #001E38; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 10px; }
.db-section-icon { font-size: 15px; color: #5B9BD5; }

/* ── Shelf Task Cards ────────────────────────────────────── */
.shelf-task-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 12px; margin-bottom: 20px; }
.stask-card { background: #fff; border: 1px solid #e8edf5; border-radius: 8px; overflow: hidden; cursor: pointer; display: flex; transition: box-shadow 0.2s, transform 0.15s; box-shadow: 0 1px 4px rgba(0,0,0,0.06); user-select: none; }
.stask-card:hover { box-shadow: 0 4px 14px rgba(0,0,0,0.1); transform: translateY(-1px); }
.stask-accent { width: 5px; flex-shrink: 0; }
.stask-body { flex: 1; padding: 14px 16px; }
.stask-title { font-size: 12px; font-weight: 600; color: #636363; text-transform: uppercase; letter-spacing: 0.4px; margin-bottom: 8px; }
.stask-pct { font-size: 28px; font-weight: 700; color: #001E38; line-height: 1; margin-bottom: 4px; }
.stask-count { font-size: 13px; color: #252b37; font-weight: 500; }
.stask-count span { color: #9aa0ac; font-weight: 400; }

/* ── Two-col header ──────────────────────────────────────── */
.db-two-col-header { display: grid; grid-template-columns: 1fr 3fr; gap: 12px; margin-bottom: 10px; }

/* ── Info Summary Grid ───────────────────────────────────── */
.info-summary-grid { display: grid; grid-template-columns: 1fr repeat(3, minmax(0, 1fr)); gap: 12px; margin-bottom: 20px; }
.info-grid-card { background: #fff; border: 1px solid #e8edf5; border-radius: 8px; box-shadow: 0 1px 4px rgba(0,0,0,0.05); }
.shelf-info-split { display: flex; min-height: 140px; align-items: stretch; }
.shelf-info-left { width: 40%; border-right: 1px solid #e8edf5; display: flex; flex-direction: column; justify-content: center; }
.shelf-info-right { flex: 1; display: flex; flex-direction: column; justify-content: center; }
.ig-label { font-size: 11px; font-weight: 700; color: #9aa0ac; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 6px; }
.ig-value { font-size: 32px; font-weight: 700; color: #001E38; line-height: 1; }
.ig-sublabel { font-size: 11px; font-weight: 600; color: #9aa0ac; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 4px; }
.ig-subvalue { font-size: 22px; font-weight: 700; color: #252b37; }
.vision-coverage-row { display: flex; gap: 28px; }

/* ── Health Cards ────────────────────────────────────────── */
.hc-title { display: flex; align-items: center; gap: 6px; font-size: 12px; font-weight: 700; color: #001E38; text-transform: uppercase; letter-spacing: 0.5px; }
.hc-icon { font-size: 15px; color: #5B9BD5; }
.health-card-stats { display: flex; gap: 0; }
.health-stat-item { flex: 1; text-align: center; border-right: 1px solid #e8edf5; }
.no-border-right { border-right: none; }
.hc-badge { display: inline-block; font-size: 10px; font-weight: 700; letter-spacing: 0.4px; text-transform: uppercase; padding: 2px 7px; border-radius: 10px; background: #f0f1f3; color: #636363; margin-bottom: 6px; }
.hc-badge--good { background: #e6f9ea; color: #35a142; }
.hc-badge--bad  { background: #fdecea; color: #e76067; }
.health-number { font-size: 26px; font-weight: 700; color: #252b37; }
.greenColor { color: #35a142; }
.colorRed   { color: #e76067; }

/* ── Edge Device ─────────────────────────────────────────── */
.edge-device-info { display: flex; flex-direction: column; gap: 14px; }
.edge-row { display: flex; justify-content: space-between; align-items: center; }
.edge-key { font-size: 11px; font-weight: 700; color: #9aa0ac; text-transform: uppercase; letter-spacing: 0.4px; }
.edge-val { font-size: 13px; font-weight: 700; color: #252b37; }
.edge-status { display: flex; align-items: center; gap: 4px; color: #9aa0ac; }
.edge-status--ok { color: #35a142; }

/* ── Operation Info ──────────────────────────────────────── */
.operation-info-row { display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 12px; }
.operation-card { background: #fff; border: 1px solid #e8edf5; border-radius: 8px; padding: 16px 20px; box-shadow: 0 1px 4px rgba(0,0,0,0.05); }
.op-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px; }
.op-label { font-size: 11px; font-weight: 700; color: #9aa0ac; text-transform: uppercase; letter-spacing: 0.5px; }
.op-trend { display: flex; align-items: center; gap: 3px; font-size: 11px; font-weight: 700; padding: 2px 7px; border-radius: 10px; }
.op-trend--up   { background: #e6f9ea; color: #35a142; }
.op-trend--down { background: #fdecea; color: #e76067; }
.op-value { font-size: 30px; font-weight: 700; color: #001E38; line-height: 1; margin-bottom: 6px; }
.op-avg { font-size: 12px; color: #9aa0ac; }

/* ── Shelf History ───────────────────────────────────────── */
.shelf-history-card { background: #fff; border: 1px solid #e8edf5; border-radius: 8px; padding: 18px 20px; box-shadow: 0 1px 4px rgba(0,0,0,0.05); }
.shelf-history-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px; }
.shelf-history-controls { display: flex; align-items: center; gap: 10px; }
.range-switcher { display: flex; background: #f0f1f3; border-radius: 6px; padding: 3px; gap: 2px; }
.range-btn { border: none; background: transparent; padding: 4px 12px; cursor: pointer; font-size: 12px; font-weight: 600; color: #9aa0ac; border-radius: 4px; transition: all 0.15s; }
.range-btn--active { background: #001E38; color: #fff; box-shadow: 0 1px 4px rgba(0,0,0,0.15); }
.date-picker-wrapper { max-width: 128px; }
.empty-chart-state { display: flex; flex-direction: column; align-items: center; justify-content: center; height: 320px; color: #c0c4cc; background: #fafafa; border-radius: 6px; }
.empty-chart-icon { font-size: 52px; margin-bottom: 8px; }
.empty-text { font-size: 13px; font-weight: 500; margin: 0; color: #b0b5be; }

/* ── Tab 1 ───────────────────────────────────────────────── */
.dashboard-card { background: #fff; border: 1px solid #e8edf5; border-radius: 8px; padding: 16px; box-shadow: 0 1px 4px rgba(0,0,0,0.05); }
.card-title { font-size: 12px; font-weight: 700; color: #001E38; text-transform: uppercase; letter-spacing: 0.4px; padding: 10px; text-align: center; }
.score-header { display: flex; align-items: baseline; gap: 8px; margin-bottom: 10px; }
.score-value { font-size: 32px; font-weight: 700; color: #001E38; }
.dot { width: 9px; height: 9px; border-radius: 50%; display: inline-block; margin-right: 5px; }
.dot.oos { background: #E76067; }
.dot.ls  { background: #5B9BD5; }
.dot.mis { background: #E0C800; }
.floor-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px; }
.floor-toolbar { display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px; }
.legend-group { display: flex; gap: 14px; align-items: center; font-size: 12px; color: #636363; }
.floor-map-container { border: 1px solid #e8edf5; border-radius: 6px; height: 280px; overflow: hidden; display: flex; justify-content: center; align-items: center; background: #fafafa; }
.no-data { display: flex; align-items: center; justify-content: center; min-height: 48px; color: #9aa0ac; font-size: 12px; }

/* ── Form overrides ──────────────────────────────────────── */
::v-deep .form-input.v-text-field--outlined { padding-top: 0; }
::v-deep .v-text-field--outlined.v-input--dense.v-text-field--outlined > .v-input__control > .v-input__slot { min-height: 34px; }
::v-deep .tbl-type01.v-data-table thead th { background-color: #f5f6fa !important; }

/* ── Responsive ──────────────────────────────────────────── */
@media (max-width: 1400px) {
  .info-summary-grid { grid-template-columns: repeat(2, 1fr); }
  .db-two-col-header { grid-template-columns: 1fr 1fr; }
}
@media (max-width: 768px) {
  .vision-dashboard-page { padding: 12px 14px; }
  .info-summary-grid { grid-template-columns: 1fr; }
  .db-two-col-header { grid-template-columns: 1fr; }
  .shelf-info-split { flex-direction: column; }
  .shelf-info-left { width: 100%; border-right: 0; border-bottom: 1px solid #e8edf5; }
  .shelf-history-controls { flex-direction: column; align-items: stretch; }
  .operation-info-row { grid-template-columns: 1fr; }
}

/* ── OLD (kept for build compat, overridden above) ───────── */
.shelf-task-container {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  width: 100%;
  gap: 14px;
  opacity: 1;
}

.shelf-task-card {
  position: relative;
  width: 100% !important;
  min-height: 136px !important;
  height: auto !important;
  padding: 0 !important;
  background: #fff !important;
  border: 1px solid #e8e8e8 !important;
  border-radius: 4px !important;
  box-shadow: 0 1px 5px rgba(0, 0, 0, 0.1);
  cursor: pointer;
  overflow: hidden;
  transition: background-color 0.2s ease, box-shadow 0.2s ease;
  user-select: none;
}

.shelf-task-card::before {
  position: absolute;
  inset: 0;
  background-color: rgba(0,0,0,0.87);
  content: "";
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.2s;
  z-index: 0;
}

.shelf-task-card:hover::before {
  opacity: 0.04;
}

.shelf-task-card:active::before {
  opacity: 0.12;
}

.shelf-task-card dl {
  position: relative;
  height: 100%;
  z-index: 1;
}

.shelf-task-card dt {
  display: flex;
  align-items: center;
  height: 40px;
  letter-spacing: 0;
  text-transform: none;
  text-indent: 0;
  text-align: left;
  padding: 0 10px;
  border-bottom: 2px solid #cbcbcb;
  line-height: 24px;
  font-size: 14px;
  color: #252b37;
  font-style: normal;
}

.shelf-task-card dd {
  display: flex;
  justify-content: center;
  align-items: flex-end;
  flex-direction: column;
  flex: 1;
  padding: 0 10px 10px 10px;
  text-align: right;
  font-size: 14px;
  font-weight: 300;
  color: #4d4f5c;
  letter-spacing: -0.05em;
  height: 75%;
}

.shelf-task-card dd p {
  line-height: 1;
  margin: 0;
  font-size: 12px;
}

.shelf-task-card dd strong {
  font-size: 24px;
  font-weight: 700;
}

::v-deep .v-slide-group__wrapper {
  height: 34px;
  border-bottom: 1px solid #e5e7eb;
}

::v-deep .v-slide-group__content {
  gap: 18px;
}

.tabs {
  text-transform: none;
  color: #2d2e7b;
  min-width: 0;
  padding: 0;
}

::v-deep .v-tabs-slider-wrapper {
  height: 2px !important;
  color: #2d2e7b;
}

::v-deep .theme--light.v-tabs > .v-tabs-bar .v-tab:not(.v-tab--active) {
  background-color: #fff !important;
}

.info-summary-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  grid-template-areas:
    "shelf-title device-title device-title device-title"
    "shelf-card connectivity battery edge";
  grid-template-rows: auto minmax(156px, auto);
  gap: 14px;
  width: 100%;
}

.info-title-shelf {
  grid-area: shelf-title;
}

.info-title-device {
  grid-area: device-title;
}

.shelf-info-card {
  grid-area: shelf-card;
}

.device-health-card:nth-of-type(2) {
  grid-area: connectivity;
}

.device-health-card:nth-of-type(3) {
  grid-area: battery;
}

.device-health-card:nth-of-type(4) {
  grid-area: edge;
}

.info-section-title {
  font-size: 16px;
  font-weight: 600;
  color: #161616;
}

.info-grid-card {
  background: #fff;
  border: 1px solid #e8edf5;
  border-radius: 10px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}

.shelf-info-split {
  height: auto;
  min-height: 156px;
  display: flex;
  align-items: stretch;
}

.shelf-info-left {
  width: 33%;
  border-right: 1px solid #e8e8e8;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.shelf-info-right {
  width: 67%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.shelf-info-sub-label,
.health-label-small,
.edge-label {
  color: #636363;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0;
  font-weight: 600;
  font-style: normal;
  white-space: nowrap;
}

.shelf-info-value {
  font-size: 28px;
  font-weight: 700;
  color: #222;
  margin-top: 8px;
  line-height: 36px;
}

.vision-heading {
  font-size: 16px;
  line-height: 24px;
  font-weight: 600;
  font-style: normal;
  color: #252b37;
  letter-spacing: 0;
}

.vision-coverage-row {
  display: flex;
  gap: 32px;
}

.shelf-info-sub-value {
  font-size: 24px;
  font-weight: 600;
  margin-top: 6px;
}

.health-card-title {
  font-size: 16px;
  line-height: 24px;
  font-weight: 600;
  font-style: normal;
  color: #252b37;
  letter-spacing: 0;
  margin-bottom: 18px;
}

.health-card-stats {
  display: flex;
  margin-top: 30px;
}

.health-stat-item {
  flex: 1;
  text-align: center;
  border-right: 1px solid #e8edf5;
}

.no-border-right {
  border-right: none;
}

.health-number {
  font-size: 24px;
  font-weight: 700;
  margin-top: 6px;
}

.greenColor {
  color: #35a142;
}

.colorRed {
  color: #e76067;
}

.edge-device-info {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 30px;
}

.edge-info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.edge-value {
  font-weight: 600;
  color: #222;
}

.operation-info-title {
  font-size: 16px;
  font-weight: 600;
  color: #161616;
  margin-bottom: 14px;
}

.operation-info-row {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
}

.operation-card {
  border: 1px solid #e8edf5;
  border-radius: 10px;
  background: #fff;
  padding: 18px;
  height: 100%;
}

.operation-card-label {
  font-size: 16px;
  line-height: 24px;
  color: #252b37;
  font-weight: 600;
  font-style: normal;
  letter-spacing: 0;
  margin-bottom: 10px;
}

.operation-metric-row {
  display: flex;
  align-items: end;
  gap: 7px;
}

.operation-value {
  font-size: 28px;
  font-weight: 700;
  color: #222;
}

.operation-average {
  font-size: 12px;
  color: #7b8794;
  margin-bottom: 9px;
}

.operation-growth {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  font-weight: 600;
}

.operation-trend-icon {
  font-size: 16px;
}

.greenColor {
  color: #35a142;
}

.colorRed {
  color: #e74c3c;
}

.shelf-history-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.shelf-history-card {
  background: #fff;
  border: 1px solid #e8edf5;
  border-radius: 10px;
  padding: 20px;
}

.shelf-history-title {
  font-size: 16px;
  font-weight: 600;
}

.shelf-history-controls {
  display: flex;
  align-items: center;
  gap: 12px;
}

.history-range-switcher {
  display: flex;
  background: #f3f3f3;
  border-radius: 4px;
  overflow: hidden;
  padding: 2px;
  border: 1px solid #e8e8e8;
}

.history-range-btn {
  border: none;
  background: transparent;
  padding: 6px 14px;
  cursor: pointer;
  font-size: 12px;
  color: #666;
  transition: all 0.2s;
}

.history-range-btn-active {
  background: #fff;
  color: #252b37;
  font-weight: 600;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
}

::v-deep .form-input.v-text-field--outlined {
  padding-top: 0;
}

::v-deep
  .v-text-field--outlined.v-input--dense.v-text-field--outlined
  > .v-input__control
  > .v-input__slot {
  min-height: 35px;
}

.date-picker-wrapper {
  max-width: 128px;
}

::v-deep .apexcharts-legend-marker svg {
  border: 2px solid transparent;
  border-radius: 5px;
}

::v-deep .apexcharts-legend-marker {
  height: 14px !important;
  width: 14px !important;
}

::v-deep .apexcharts-legend-series {
  gap: 3px;
}

.dashboard-card {
  background: #fff;
  border: 1px solid #e8edf5;
  border-radius: 10px;
  padding: 16px;
}

.card-title {
  padding-right: 5px;
  padding-left: 5px;
  padding-bottom: 5px;
  font-weight: 300 !important;
  font-size: 16px;
  white-space: nowrap;
  display: flex;
  justify-content: center;
}

.score-header {
  display: flex;
  align-items: center;
  gap: 10px;
}

.score-value {
  font-size: 28px;
  font-weight: 700;
}

.greenColor {
  color: #1cb11c;
}

.circle {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  display: inline-block;
  margin-right: 5px;
}

.oos {
  background: #f0806f;
}

.ls {
  background: #75b0dd;
}

.mis {
  background: #fbaf1e;
}

.floor-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.floor-toolbar {
  display: flex;
  justify-content: space-between;
  margin: 15px 0;
}

.legend-group {
  display: flex;
  gap: 15px;
  align-items: center;
}

.zoom-actions {
  display: flex;
  justify-content: flex-end;
}

.floor-map-container {
  border: 1px solid #ccc;
  height: 300px;
  overflow: hidden;
  display: flex;
  justify-content: center;
  align-items: center;
}

::v-deep v-data-table__empty-wrapper td {
  padding: 0;
}

::v-deep .tbl-type01.v-data-table thead th {
  background-color: #ececec !important;
}

.empty-chart-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100%;
  color: #999;
  background: #f9f9f9;
  border-radius: 4px;
  height: 350px;
}

.empty-icon {
  font-size: 64px;
  margin-bottom: 8px;
  opacity: 0.5;
}

.empty-text {
  font-size: 13px;
  font-weight: 500;
  margin: 0 0 8px 0;
  color: #666;
}

.no-data {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 48px;
}

@media (max-width: 768px) {
  .shelf-info-split {
    flex-direction: column;
    align-items: flex-start;
    gap: 20px;
  }

  .info-summary-grid {
    grid-template-columns: 1fr;
    grid-template-areas:
      "shelf-title"
      "shelf-card"
      "device-title"
      "connectivity"
      "battery"
      "edge";
    grid-template-rows: auto minmax(114px, auto) auto repeat(
        3,
        minmax(114px, auto)
      );
  }

  .shelf-info-left,
  .shelf-info-right {
    width: 100% !important;
  }

  .shelf-info-left {
    border-right: 0 !important;
    border-bottom: 1px solid #e8e8e8;
  }

  .health-card-stats {
    flex-direction: column;
    gap: 16px;
  }

  .health-stat-item {
    border-right: none;
    border-bottom: 1px solid #e8edf5;
    padding-bottom: 12px;
  }

  .health-stat-item:last-child {
    border-bottom: none;
  }

  .shelf-history-controls {
    flex-direction: column;
    align-items: stretch;
  }

  .operation-info-row {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 1200px) {
  .info-summary-grid {
    grid-template-columns: repeat(2, 1fr);
    grid-template-areas:
      "shelf-title device-title"
      "shelf-card connectivity"
      "battery edge";
  }
}

@media (max-width: 1400px) {
  .info-summary-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    grid-template-areas:
      "shelf-title device-title"
      "shelf-card connectivity"
      "shelf-card battery"
      "shelf-card edge";
    grid-template-rows: auto repeat(3, minmax(114px, auto));
  }
}
</style>
